import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBackHandler } from '../hooks/useBackHandler';

/**
 * **사진 묶음** — 공지 본문 맨 위(사용자 2026-10-27).
 *
 *   격자로 모아 보인다(카톡 미리보기 그림 `backend/utils/noticeCard.ts` 와 같은 짜임):
 *     1장 = 그 사진 그대로 · 2장 = 좌우 반씩 · 3장 = 왼쪽 큰 칸 + 오른쪽 위아래 · 4장 이상 = 2x2, 넷째 칸에 "+N"
 *   칸을 누르면 화면 가득 크게 — 크게 보기에서는 옆으로 넘긴다(휴대폰은 밀고, 컴퓨터는 양옆 화살표). 누르거나 뒤로 가기로 닫는다.
 */
export interface Photo { url: string }

/** 옆으로 넘기는 띠 — 지금 몇 번째인지 알려 준다 */
const Strip: React.FC<{
  photos: Photo[];
  start?: number;
  height: number | string;
  dark?: boolean;
  onIndex?: (i: number) => void;
  onPhotoClick?: (i: number) => void;
}> = ({ photos, start = 0, height, dark, onIndex, onPhotoClick }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(start);

  useEffect(() => {
    const el = ref.current;
    if (el && start) el.scrollLeft = start * el.clientWidth;
  }, [start]);

  const go = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const n = Math.max(0, Math.min(photos.length - 1, i));
    el.scrollTo({ left: n * el.clientWidth, behavior: 'smooth' });
  };

  const many = photos.length > 1;
  const arrow = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute', top: '50%', [side]: 8, transform: 'translateY(-50%)',
    width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'rgba(0, 0, 0, 0.45)', color: '#fff', fontSize: 18, lineHeight: '34px', padding: 0
  });

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={ref}
        onScroll={(e) => {
          const el = e.currentTarget;
          const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
          if (i !== idx) { setIdx(i); onIndex?.(i); }
        }}
        style={{
          display: 'flex', overflowX: many ? 'auto' : 'hidden', scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none', borderRadius: dark ? 0 : 12, background: dark ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
          // 사진 테두리(사용자 2026-10-27) — 크게 보기에서는 뺀다
          border: dark ? 'none' : '1.5px solid rgba(102, 126, 234, 0.3)', boxSizing: 'border-box'
        }}
      >
        {photos.map((p, i) => (
          <div key={p.url + i} style={{ flex: '0 0 100%', height, scrollSnapAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
              src={p.url}
              alt=""
              loading={i === start ? 'eager' : 'lazy'}
              onClick={(e) => { if (onPhotoClick) { e.stopPropagation(); onPhotoClick(i); } }}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', cursor: onPhotoClick ? 'zoom-in' : 'default', borderRadius: dark ? 6 : 0 }}
            />
          </div>
        ))}
      </div>
      {many && (
        <>
          {idx > 0 && <button aria-label="앞 사진" style={arrow('left')} onClick={(e) => { e.stopPropagation(); go(idx - 1); }}>‹</button>}
          {idx < photos.length - 1 && <button aria-label="다음 사진" style={arrow('right')} onClick={(e) => { e.stopPropagation(); go(idx + 1); }}>›</button>}
          <span style={{
            position: 'absolute', top: 8, right: 8, fontSize: 11, fontWeight: 700, color: '#fff',
            background: 'rgba(0, 0, 0, 0.5)', borderRadius: 10, padding: '2px 8px'
          }}>{idx + 1}/{photos.length}</span>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 8 }}>
            {photos.map((_, i) => (
              <span
                key={i}
                onClick={(e) => { e.stopPropagation(); go(i); }}
                style={{
                  width: i === idx ? 16 : 6, height: 6, borderRadius: 3, cursor: 'pointer', transition: 'width 0.2s ease',
                  background: i === idx ? (dark ? '#fff' : '#667eea') : (dark ? 'rgba(255,255,255,0.4)' : 'rgba(102, 126, 234, 0.3)')
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/** 격자 칸 자리 — grid-area 값(행 시작 / 열 시작 / 행 끝 / 열 끝) */
function areas(n: number): string[] {
  if (n === 2) return ['1 / 1 / 3 / 2', '1 / 2 / 3 / 3'];
  if (n === 3) return ['1 / 1 / 3 / 2', '1 / 2 / 2 / 3', '2 / 2 / 3 / 3'];
  return ['1 / 1 / 2 / 2', '1 / 2 / 2 / 3', '2 / 1 / 3 / 2', '2 / 2 / 3 / 3'];
}

const PhotoAlbum: React.FC<{ photos: Photo[] }> = ({ photos }) => {
  const [big, setBig] = useState<number | null>(null);
  useBackHandler(big !== null, () => setBig(null));
  if (!photos.length) return null;
  const frame: React.CSSProperties = {
    borderRadius: 12, overflow: 'hidden', border: '1.5px solid rgba(102, 126, 234, 0.3)', boxSizing: 'border-box'
  };
  const shown = photos.slice(0, 4);
  const more = photos.length - shown.length;
  return (
    <>
      {photos.length === 1 ? (
        // 한 장은 자르지 않고 그대로
        <div style={{ ...frame, display: 'inline-block', maxWidth: '100%', lineHeight: 0, background: 'rgba(0, 0, 0, 0.04)' }}>
          <img
            src={photos[0].url}
            alt=""
            onClick={() => setBig(0)}
            style={{ display: 'block', maxWidth: '100%', maxHeight: 480, objectFit: 'contain', cursor: 'zoom-in' }}
          />
        </div>
      ) : (
        <div style={{
          ...frame, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 4,
          aspectRatio: '1.91 / 1', background: 'rgba(102, 126, 234, 0.12)'
        }}>
          {shown.map((p, i) => (
            <div key={p.url + i} onClick={() => setBig(i)} style={{ gridArea: areas(shown.length)[i], position: 'relative', minHeight: 0, cursor: 'zoom-in' }}>
              {/* 자르지 않고 줄여서 다 보이게(사용자 2026-10-27) */}
              <img src={p.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: 'rgba(127, 127, 127, 0.08)' }} />
              {i === 3 && more > 0 && (
                <div style={{
                  position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.55)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800
                }}>+{more}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* body 로 띄운다 — 탭 틀에 transform 이 걸려 있으면 fixed 가 그 안에 갇혀 보이지 않는다 */}
      {big !== null && createPortal(
        <div
          onClick={() => setBig(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0, 0, 0, 0.95)',
            display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: 'zoom-out'
          }}
        >
          <Strip photos={photos} start={big} height="86vh" dark />
        </div>,
        document.body
      )}
    </>
  );
};

export default PhotoAlbum;
